import { InlineLoading, InlineNotification } from '@carbon/react';
import { InformationFilled } from '@carbon/icons-react';
import {
  domainLabel,
  domainLabels,
} from 'lib/evaluation-case/scenario-domains';
import { caseIncompleteText } from 'lib/evaluation-case/readiness-text';
import { format0 } from 'utils/number';
import type { EvaluationCaseCoverage, PendingCaseCoverage } from './types';
import styles from './case-coverage-v2.module.scss';

// The case-level coverage pieces the case picker and the case-management coverage tile add to
// their rows. Each renders nothing for a row without the case-level fields.

/**
 * "Case incomplete: missing DCA · archived Taxes" for a case a case-level run would refuse
 * (EvaluationCase::Readiness on the BE); nothing for a complete one. The same text as the eval
 * page's Calculate gate (caseIncompleteText).
 *
 * @param {object} props - The coverage row.
 * @param {Pick<EvaluationCaseCoverage, 'complete' | 'missingDomains' | 'archivedDomains'>} props.row - Its readiness fields.
 * @return {JSX.Element | null} The note, or null.
 */
export function CaseIncompleteNote({
  row,
}: {
  row: Pick<
    EvaluationCaseCoverage,
    'complete' | 'missingDomains' | 'archivedDomains'
  >;
}) {
  if (row.complete !== false) return null;

  return (
    <span className={styles.incomplete} data-testid="case-incomplete-note">
      {caseIncompleteText(row.missingDomains, row.archivedDomains) ??
        'Case incomplete'}
    </span>
  );
}

/**
 * In place of "All assets covered" on a row with nothing left to value: the TC team rejected every
 * place, so there is nothing to cover, nor anything missing.
 *
 * @param {object} props - The sentence.
 * @param {string} props.text - The row's nothingToValueText, as the server words it.
 * @return {JSX.Element} The row.
 */
export function NothingToValueRow({ text }: { text: string | undefined }) {
  return (
    <div className={styles.nothingToValue} data-testid="nothing-to-value">
      <InformationFilled
        aria-hidden="true"
        className={styles.nothingToValueIcon}
      />
      <span>{text}</span>
    </div>
  );
}

/**
 * The slots each assigned shape-carrying domain misses ("Taxes 12 · Expenses 40"), domains that
 * miss none left out; nothing when no domain misses any.
 *
 * @param {object} props - The per-domain counts.
 * @param {Record<string, number> | undefined} props.domainMissing - Slots missed, by registry key.
 * @return {JSX.Element | null} The line, or null.
 */
export function DomainMissingLine({
  domainMissing,
}: {
  domainMissing: Record<string, number> | undefined;
}) {
  const missed = Object.entries(domainMissing ?? {}).filter(
    ([, count]) => count > 0
  );
  if (missed.length === 0) return null;

  return (
    <span className={styles.domainMissing} data-testid="domain-missing-line">
      Slots missed by{' '}
      {missed
        .map(([key, count]) => `${domainLabel(key)} ${format0(count)}`)
        .join(' · ')}
    </span>
  );
}

/**
 * The cases a case-level coverage list has not counted yet: still calculating (with what they wait
 * on), or given up on (with why). Neither can be picked until it is counted.
 *
 * @param {object} props - The pending rows.
 * @param {PendingCaseCoverage[]} props.calculating - Rows still being computed.
 * @param {PendingCaseCoverage[]} props.unavailable - Rows whose computation gave up.
 * @return {JSX.Element | null} The list, or null when nothing is pending.
 */
export function PendingCoverageList({
  calculating,
  unavailable,
}: {
  calculating: PendingCaseCoverage[];
  unavailable: PendingCaseCoverage[];
}) {
  if (calculating.length === 0 && unavailable.length === 0) return null;

  return (
    <div className={styles.pending} aria-label="Cases not counted yet">
      {calculating.map((row) => (
        <InlineLoading
          key={row.id}
          description={`${row.name}: calculating coverage${
            row.pendingDomains?.length
              ? ` for ${domainLabels(row.pendingDomains)}`
              : ''
          }…`}
        />
      ))}
      {unavailable.map((row) => (
        <InlineNotification
          key={row.id}
          kind="warning"
          lowContrast
          hideCloseButton
          title={row.name}
          subtitle={
            row.error?.message ??
            'Coverage could not be computed for this case.'
          }
        />
      ))}
    </div>
  );
}
